import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { User, GraduationCap, Building2, Calendar, ArrowRight, CheckCircle } from "lucide-react";
import falcoLogo from "@/assets/falco.jpeg";



// College list for RGUHS
const COLLEGES = [
  "Bangalore Medical College and Research Institute (BMCRI)",
  "M.S. Ramaiah Medical College",
  "Kempegowda Institute of Medical Sciences (KIMS)",
  "St. John's Medical College",
  "JSS Medical College, Mysore",
  "Kasturba Medical College, Mangalore",
  "Kasturba Medical College, Manipal",
  "SDM College of Medical Sciences, Dharwad",
  "Karnataka Institute of Medical Sciences (KIMS), Hubli",
  "Jawaharlal Nehru Medical College (JNMC), Belgaum",
  "Mysore Medical College and Research Institute",
  "Shimoga Institute of Medical Sciences",
  "Vijayanagar Institute of Medical Sciences (VIMS), Bellary",
  "Rajarajeswari Medical College and Hospital",
  "Vydehi Institute of Medical Sciences",
  "MVJ Medical College and Research Hospital",
  "Sapthagiri Institute of Medical Sciences",
  "East Point College of Medical Sciences",
  "BGS Global Institute of Medical Sciences",
  "Adichunchanagiri Institute of Medical Sciences",
  "Other RGUHS College"
];

const ProfileCompletionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuth();
  
  const [formData, setFormData] = useState({
    name: user?.name || "",
    phone: "",
    college: "",
    semester: "1",
    rollNumber: ""
  });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Personal, 2: Academic

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    if (!formData.name.trim()) {
      toast.error("Please enter your full name");
      return false;
    }
    if (!formData.phone.trim() || formData.phone.length < 10) {
      toast.error("Please enter a valid phone number");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.college) {
      toast.error("Please select your college");
      return false;
    }
    if (!formData.semester) {
      toast.error("Please select your semester");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    
    setLoading(true);
    try {
      const response = await axios.put(
        `${API}/users/me/profile`,
        {
          name: formData.name,
          phone: formData.phone,
          college: formData.college,
          semester: parseInt(formData.semester),
          roll_number: formData.rollNumber || null
        }
      );
      
      setUser(response.data);
      toast.success("Profile completed successfully!");
      navigate("/onboarding", { replace: true });
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to save profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2E3A59] to-[#1A2236] p-6 text-center">
          <img src={falcoLogo} alt="Falco" className="h-12 mx-auto mb-3" />
          <h1 className="text-xl font-heading font-bold text-white">Complete Your Profile</h1>
          <p className="text-slate-300 text-sm mt-1">Help us personalize your experience</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-3 py-4 bg-slate-50 border-b">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#2E3A59]' : 'text-slate-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= 1 ? 'bg-[#2E3A59] text-white' : 'bg-slate-200'
            }`}>
              {step > 1 ? <CheckCircle className="w-4 h-4" /> : '1'}
            </div>
            <span className="text-sm font-medium">Personal</span>
          </div>
          <div className="w-8 h-0.5 bg-slate-200"></div>
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#2E3A59]' : 'text-slate-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= 2 ? 'bg-[#2E3A59] text-white' : 'bg-slate-200'
            }`}>
              2
            </div>
            <span className="text-sm font-medium">Academic</span>
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Personal Info */}
          {step === 1 && (
            <div className="space-y-5 animate-fade-in" data-testid="profile-step-1">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Full Name *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter your full name"
                  className="mt-1.5"
                  data-testid="profile-name-input"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                  Phone Number *
                </Label>
                <div className="flex mt-1.5">
                  <span className="inline-flex items-center px-3 bg-slate-100 border border-r-0 border-slate-200 rounded-l-md text-slate-500 text-sm">
                    +91
                  </span>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="9876543210"
                    className="rounded-l-none"
                    data-testid="profile-phone-input"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">We'll send important updates here</p>
              </div>

              <Button
                onClick={handleNext}
                className="w-full h-12 bg-[#2E3A59] hover:bg-[#1A2236] font-semibold btn-press"
                data-testid="profile-next-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 2: Academic Info */}
          {step === 2 && (
            <div className="space-y-5 animate-fade-in" data-testid="profile-step-2">
              <div>
                <Label htmlFor="college" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  College / Institution *
                </Label>
                <Select
                  value={formData.college}
                  onValueChange={(value) => handleInputChange("college", value)}
                >
                  <SelectTrigger className="mt-1.5" data-testid="profile-college-select">
                    <SelectValue placeholder="Select your college" />
                  </SelectTrigger>
                  <SelectContent>
                    {COLLEGES.map((college) => (
                      <SelectItem key={college} value={college}>
                        {college}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="semester" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <GraduationCap className="w-4 h-4" />
                  Current Semester *
                </Label>
                <Select
                  value={formData.semester}
                  onValueChange={(value) => handleInputChange("semester", value)}
                >
                  <SelectTrigger className="mt-1.5" data-testid="profile-semester-select">
                    <SelectValue placeholder="Select semester" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Semester 1 (Anatomy, Biochemistry, Physiology)</SelectItem>
                    <SelectItem value="2">Semester 2 (Anatomy, Biochemistry, Physiology)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">Questions will be filtered based on your semester</p>
              </div>

              <div>
                <Label htmlFor="rollNumber" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Roll Number (Optional)
                </Label>
                <Input
                  id="rollNumber"
                  value={formData.rollNumber}
                  onChange={(e) => handleInputChange("rollNumber", e.target.value)}
                  placeholder="e.g., 2024MBBS001"
                  className="mt-1.5"
                  data-testid="profile-roll-input"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1"
                  data-testid="profile-back-btn"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-[#C9A227] hover:bg-[#A6851D] text-[#1A2236] font-semibold btn-press"
                  data-testid="profile-submit-btn"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-[#1A2236] border-t-transparent rounded-full animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Complete Profile
                      <CheckCircle className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <p className="text-xs text-slate-400 text-center">
            Your information is secure and will only be used to personalize your learning experience.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionPage;
